import { t as getLogger } from "./logger-CpoV6RQj.mjs";

//#region src/types/errors.ts
/**
* Creates a network error with additional metadata
* @param message - Error message
* @param url - URL that was being accessed
* @param isMonitored - Whether the URL is a monitored service
* @param originalError - Original error if this is a wrapped error
* @returns Network error with metadata
*/
function createNetworkError(message, url, isMonitored, originalError) {
	const error = new Error(message);
	error.url = url;
	error.isMonitored = isMonitored;
	error.originalError = originalError;
	return error;
}
/**
* Creates a slow request warning with additional metadata
* @param message - Warning message
* @param url - URL that was being accessed
* @param isMonitored - Whether the URL is a monitored service
* @param timeoutMs - Timeout in milliseconds
* @returns Network error with metadata
*/
function createSlowRequestWarning(message, url, isMonitored, timeoutMs) {
	const error = createNetworkError(message, url, isMonitored);
	error.name = "SlowRequestWarning";
	error.isMonitored = isMonitored;
	error.timeoutMs = timeoutMs;
	return error;
}
/**
* Type guard to check if an error is a SlowRequestWarning
*
* @param error - The error to check
* @returns True if the error is a SlowRequestWarning
*/
function isSlowRequestWarning(error) {
	return error.name === "SlowRequestWarning";
}

//#endregion
//#region src/utils/global.ts
/**
* A constant that holds a reference to the global object.
* It uses `globalThis` if available, with a fallback to `window` for older browsers.
* This ensures compatibility across different environments.
*/
const getGlobal = () => {
	if (typeof globalThis !== "undefined") return globalThis;
	if (typeof window !== "undefined") return window;
	if (typeof global !== "undefined") return global;
	return {};
};
/**
* A constant that holds a reference to the global object, derived from `getGlobal`.
*/
const WINDOW = getGlobal();

//#endregion
//#region src/managers/errorManager.ts
/**
* Manages triggering status checks based on network errors.
* Features:
* - Triggers checks for specific error types on monitored URLs.
* - Throttles status checks to prevent rapid firing during incidents.
*/
var ErrorManager = class ErrorManager {
	static instance;
	logger = getLogger();
	statusCheckTrigger;
	lastStatusTrigger = 0;
	statusTriggerThrottle = 3e3;
	constructor() {}
	static getInstance() {
		if (!ErrorManager.instance) ErrorManager.instance = new ErrorManager();
		return ErrorManager.instance;
	}
	/**
	* Set the function that will be called to trigger a status check.
	* @param trigger - The function to call to initiate a status check (after throttling).
	*/
	setStatusCheckTrigger(trigger) {
		this.statusCheckTrigger = trigger;
	}
	/**
	* Handle an error received from an interceptor or other source.
	*/
	handleError(error, source) {
		this.logger.debug(`Received error from ${source}, evaluating for status check`, { error });
		this._evaluateAndTriggerStatusCheck(error, source);
	}
	/**
	* Evaluates if an error warrants a status check, applies throttling, and triggers if appropriate.
	*/
	_evaluateAndTriggerStatusCheck(error, source) {
		if (!error.isMonitored) {
			this.logger.debug("Status check skipped: URL not monitored", {
				url: error.url,
				source
			});
			return;
		}
		const isSlow = isSlowRequestWarning(error);
		const is5xx = "status" in error && typeof error.status === "number" && error.status >= 500 && error.status < 600;
		const isNetworkError = !("status" in error) || typeof error.status !== "number";
		if (!(isSlow || is5xx || isNetworkError)) {
			this.logger.debug("Status check skipped: Error type does not meet criteria on monitored URL", {
				error,
				source
			});
			return;
		}
		this.logger.debug("Error meets criteria for potential status check trigger", {
			error,
			source,
			is5xx,
			isSlow
		});
		const now = Date.now();
		if (now - this.lastStatusTrigger < this.statusTriggerThrottle) {
			const waitTimeMs = this.statusTriggerThrottle - (now - this.lastStatusTrigger);
			this.logger.debug("Status check throttled - too soon since last trigger", {
				source,
				waitTimeMs
			});
			return;
		}
		if (!this.statusCheckTrigger) {
			this.logger.warn("Status check should trigger but no statusCheckTrigger handler is set", { source });
			return;
		}
		this.lastStatusTrigger = now;
		this.logger.debug("Triggering status check", {
			error,
			source
		});
		try {
			this.statusCheckTrigger();
		} catch (triggerError) {
			this.logger.error("Error calling statusCheckTrigger:", {
				triggerError,
				source
			});
		}
	}
	/**
	* Set the status check throttle time.
	*/
	setStatusTriggerThrottle(throttleMs) {
		const oldThrottle = this.statusTriggerThrottle;
		this.statusTriggerThrottle = Math.max(throttleMs, 1e3);
		this.logger.debug("Status trigger throttle updated", {
			oldThrottle,
			newThrottle: this.statusTriggerThrottle
		});
	}
};
/**
* Get the shared error manager instance.
*/
function getErrorManager() {
	return ErrorManager.getInstance();
}

//#endregion
export { createSlowRequestWarning as i, WINDOW as n, createNetworkError as r, getErrorManager as t };